import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check } from 'lucide-react';
import { SUPPORTED_LANGUAGES } from '../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : `${window.location.origin}/api`;

const LanguageSwitcher = ({ compact = false }) => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const change = async (code) => {
    i18n.changeLanguage(code);
    try { localStorage.setItem('smartkick_language', code); } catch { /* ignore */ }
    setOpen(false);

    // Persist to user profile if logged in (best-effort, never blocks UI)
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await fetch(`${API_URL}/auth/preferences`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ language: code }),
        });
      }
    } catch { /* ignore */ }
  };

  const current = SUPPORTED_LANGUAGES.find(l => l.code === (i18n.language?.split('-')[0] || 'en')) || SUPPORTED_LANGUAGES[0];

  return (
    <div className="relative" ref={wrapRef} data-testid="language-switcher">
      <button
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-1.5 rounded-full hover:bg-white/5 transition-colors ${compact ? 'p-1.5' : 'px-2.5 py-1.5'}`}
        data-testid="lang-toggle"
        aria-label="Change language"
      >
        <Globe className="w-4 h-4 text-zinc-400" />
        {!compact && <span className="text-xs font-bold text-zinc-300 uppercase">{current.code}</span>}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-[80] bg-[#111114] border border-white/10 rounded-xl shadow-xl overflow-hidden min-w-[140px]" data-testid="lang-menu">
          {SUPPORTED_LANGUAGES.map(lang => (
            <button
              key={lang.code}
              onClick={() => change(lang.code)}
              className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-white/5 transition-colors ${current.code === lang.code ? 'text-emerald-400' : 'text-zinc-300'}`}
              data-testid={`lang-${lang.code}`}
            >
              <span className="flex items-center gap-2"><span>{lang.flag}</span>{lang.label}</span>
              {current.code === lang.code && <Check className="w-3.5 h-3.5" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
